import { useState, useCallback } from 'react';
import { Eye, Download, Printer } from 'lucide-react';
import { ButtonGroup } from '../ButtonGroup/ButtonGroup';
import { Button } from '../Button/Button';
import { apiClient } from '../../../api/client';
import { useToast } from '../../../hooks/useToast';

interface PdfActionsProps {
  /** Relative endpoint path, e.g. /receipts/:enc_id/pdf */
  endpoint: string;
  /** Suggested filename for the download, e.g. receipt-REC-20260504-001.pdf */
  filename: string;
  /** Custom print handler; falls back to window.print() if omitted. */
  onPrint?: () => void;
}

async function fetchPdfBlob(endpoint: string): Promise<Blob> {
  const res = await apiClient.raw.get<Blob>(endpoint, { responseType: 'blob' });
  return res.data;
}

export function PdfActions({ endpoint, filename, onPrint }: PdfActionsProps): JSX.Element {
  const toast = useToast();
  const [viewLoading, setViewLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const handleView = useCallback(async () => {
    setViewLoading(true);
    try {
      const blob = await fetchPdfBlob(endpoint);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke after a minute — enough time for the tab to load the blob.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error('Failed to open PDF. Please try again.');
    } finally {
      setViewLoading(false);
    }
  }, [endpoint, toast]);

  const handleDownload = useCallback(async () => {
    setDownloadLoading(true);
    try {
      const blob = await fetchPdfBlob(endpoint);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch {
      toast.error('Failed to download PDF. Please try again.');
    } finally {
      setDownloadLoading(false);
    }
  }, [endpoint, filename, toast]);

  return (
    <ButtonGroup>
      <Button
        variant="secondary"
        size="sm"
        icon={Eye}
        onClick={() => void handleView()}
        loading={viewLoading}
      >
        View
      </Button>
      <Button
        variant="secondary"
        size="sm"
        icon={Download}
        onClick={() => void handleDownload()}
        loading={downloadLoading}
      >
        Download
      </Button>
      <Button
        variant="secondary"
        size="sm"
        icon={Printer}
        onClick={onPrint ?? (() => window.print())}
      >
        Print
      </Button>
    </ButtonGroup>
  );
}
